import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { IOrderPersonalization, IProductDetailModel } from "@types";
import PersonalizationCartCard from "@elements/productCard/personalizationCartCard";
import { useKeenSlider } from "keen-slider/react";
import { useAppContext } from "@context/app";
import { segmentEvent } from "@utils/segment";

interface PersonalizationsProps {
    personalizations: IOrderPersonalization[];
    personalizationSource: string;
}

interface TabConfig {
    key: string;
    title: string;
    description?: string;
}

const Tabs: React.FC<{
    tabs: TabConfig[];
    active: string;
    onChange: (key: string) => void;
}> = ({ tabs, active, onChange }) => {
    return (
        <div className="w-full flex flex-wrap items-center gap-4">
            {tabs.map((tab) => (
                <button
                    key={tab.key}
                    onClick={() => onChange(tab.key)}
                    className={`pb-1/2 border-b-[3px] transition-colors ${active === tab.key
                        ? "text-button-primary border-button-primary"
                        : "text-[#7B5E4C] border-transparent"
                        }`}
                >
                    {tab.title}
                </button>
            ))}
        </div>
    );
};

const ProductList: React.FC<{
    products: IProductDetailModel[];
    tab: string;
    personalizationTitle?: string;
    personalizationType?: string;
    personalizationSource: string;
}> = ({ products, tab, personalizationTitle, personalizationType, personalizationSource }) => {
    const { cart } = useAppContext();
    const { locale } = useRouter();

    const [sliderRef, sliderInstanceRef] = useKeenSlider<HTMLDivElement>({
        loop: false,
        rubberband: false,
        mode: "free-snap",
        renderMode: "performance",
        rtl: locale?.includes("en-") ? false : true,
        slides: { perView: "auto", spacing: 10 },
    }, []);

    const getCartQuantity = (product: IProductDetailModel) => {
        const cartItem = cart.find((item) => item.id === product.default_sku_id);
        return cartItem?.qty || 0;
    };

    useEffect(() => {
        if (sliderInstanceRef.current) {
            sliderInstanceRef.current.update();
        }
    }, [products, sliderInstanceRef]);


    if (!products?.length) return null;

    return (
        <div className="w-full overflow-hidden mt-5">
            <div key={tab} ref={sliderRef} className="keen-slider max-w-full py-2">
                {products.map((product, index) => (
                    <div key={`${product.id}-${product.default_sku_id}-${index}`} className="keen-slider__slide max-w-[194px] min-w-[194px]">
                        <PersonalizationCartCard
                            product={product}
                            width={194}
                            source="cart"
                            section="Personalizations"
                            cartQuantity={getCartQuantity(product)}
                            personalizationTitle={personalizationTitle}
                            personalizationType={personalizationType}
                            personalizationSource={personalizationSource}
                            personalizationItemIndex={index}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

const Personalizations: React.FC<PersonalizationsProps> = ({ personalizations, personalizationSource }) => {
    const { area, country, user, eventProperties } = useAppContext();
    const { locale } = useRouter();
    const [active, setActive] = useState<string>(() => {
        // Find first personalization that has products
        const firstWithProducts = personalizations?.find(p => p.products && p.products.length > 0);
        return firstWithProducts?.type || "";
    });

    const tabs: TabConfig[] = useMemo(() => {
        const map = new Map<string, TabConfig>();
        for (const p of personalizations || []) {
            // Only include personalizations that have products
            if (p.products && p.products.length > 0) {
                const title = p.title;
                if (!map.has(p.type)) {
                    map.set(p.type, { key: p.type, title, description: p.description });
                }
            }
        }
        return Array.from(map.values());
    }, [personalizations]);

    const activePersonalization = useMemo(() => {
        return personalizations?.find((p) => p.type === active);
    }, [active, personalizations]);

    useEffect(() => {
        if (tabs.length > 0) {
            // If current active tab doesn't exist in filtered tabs, switch to first available tab
            if (!tabs.some(tab => tab.key === active)) {
                setActive(tabs[0].key);
            }
        }
    }, [personalizations, tabs, active]);

    const updateActiveTab = (newTab:string) => {
        const selectedTab = tabs.find(t => t.key === newTab);
        segmentEvent("Personalization Tab Selected", {
            personalization_title: selectedTab?.title,
            personalization_type: newTab,
            area_id: area?.id,
            area_slug: area?.slug,
            country_id: country?.id,
            country_slug: country?.slug,
            environment: process.env.NEXT_PUBLIC_SENTRY_ENV,
            currency: country?.currency_iso.toUpperCase(),
            ...eventProperties,
            language: locale?.includes("en") ? "English" : "Arabic",
            user_id: user?.data?.id || -1,
        });
        setActive(newTab);
    }

    if (!tabs?.length) return null;

    return (
        <div className="w-full mt-6">

            <div className="mt-4">
                <Tabs tabs={tabs} active={active} onChange={updateActiveTab} />
            </div>
            <div className="w-full py-3 px-4 rounded-xl mt-4 bg-[#F8D5F3] text-center">
                <p className="font-extrabold text-lg text-[#732980]">
                    {activePersonalization?.description}
                </p>
            </div>
            <ProductList
                products={activePersonalization?.products || []}
                tab={active}
                personalizationSource={personalizationSource || ""}
                personalizationTitle={activePersonalization?.title}
                personalizationType={activePersonalization?.type}
            />
        </div>
    );
};

export default Personalizations;

